import { useEffect, useState, useRef, lazy, Suspense } from 'react';
import { usePermissions } from '../contexts/PermissionContext';
import { sendToTerminal, logger } from '../lib/terminalLogger';
import { LogCategory } from '../config/logging';
import { TokenService } from '../lib/tokenService';
import { socketService } from '../lib/socketService';
import { useNotifications } from '../components/feedback/NotificationProvider';
import { OneReguaPage } from './Tools/OneReguaPage';
import { OneBitmapPage } from './Tools/OneBitmapPage';
import { OneBitmapV1Page } from './Tools/OneBitmapV1Page';
import { MontaTechPage } from './Tools/MontaTech';
import { MoldeOnePage } from './Tools/MoldeOne';
import { TestSmbPage } from './Tools/TestSmbPage';
// Lazy-load: evita dependência estática tools → tools-quotation (circular chunk)
const QuotationCalculatorPage = lazy(() =>
  import('./Tools/QuotationCalculator').then(m => ({ default: m.QuotationCalculatorPage }))
);
import { OneResizePage } from './Tools/OneResizePage';
import { OneContornoPage } from './Tools/OneContornoPage';
import { corelCommunication } from '../lib/corel/communication';
import { useHealthStatus } from '../hooks/useHealthStatus';
import { useKeyboardContext, isTypingTarget } from '../contexts/KeyboardContext';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { ShortcutConfig } from './Tools/ShortcutConfig';
import { ErrorModal } from './feedback/ErrorModal';
import { GlobalFooter } from './GlobalFooter';
import { notifyUserFriendlyError, formatErrorMessage } from '../lib/userFriendlyErrors';
import { useSsoFragmentLogin } from '../hooks/useSsoFragmentLogin';
import { setShortSsoToken, setBaseBearer } from '../lib/toolsSso';
import {
  RunPsConfirmModal,
  RefreshConfirmModal,
  ShortcutsToggle,
  CollectMetadataModal,
  ToolPageModal,
  ToolsHeader,
  ToolsGrid,
  OutdatedBanner,
  SecurityGate
} from './ToolsMenu/components';
import { CloseDocumentConfirmModal } from './ToolsMenu/components/CloseDocumentConfirmModal';
import { ExportProducaoOfflineModal } from './ToolsMenu/components/ExportProducaoOfflineModal';
import { ConfirmModal } from './ui';
import { useExportProducaoOffline } from './ToolsMenu/hooks/useExportProducaoOffline';
import {
  useToolsState,
  useToolExecution,
  useBootstrapEvents,
  useSecurityValidator,
  useToolExecutorRegistry
} from './ToolsMenu/hooks';
import { toolsRegistry } from './ToolsMenu/config/tools.registry';
import {
  syncShortcutsWithVerification,
  disableShortcutsInBackend,
  disableShortcutsWithVerification,
  parseShortcutAction,
  type SyncResult,
  type ShortcutRegistration
} from '../lib/corel/shortcuts';
import { useShortcutsLoader } from '../hooks/useShortcutsLoader';
import { useShortcutDeduplication } from '../hooks/useShortcutDeduplication';

// Util: comparação simples de versões x.y.z.w
function isVersionAtLeast(current: string, required: string): boolean {
  const a = (current || '').split('.').map((n) => parseInt(n, 10) || 0);
  const b = (required || '').split('.').map((n) => parseInt(n, 10) || 0);
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const ai = a[i] ?? 0;
    const bi = b[i] ?? 0;
    if (ai > bi) return true;
    if (ai < bi) return false;
  }
  return true;
}


export function ToolsMenu() {
  const { userInfo } = usePermissions();
  const { registerExecutor, unregisterExecutor, execute: executeShortcut, hydrate, state: keyboardContextState } = useKeyboardContext();

  // Hook para carregar atalhos da API (sem fallback para localStorage)
  const shortcutsLoader = useShortcutsLoader();
  const { notify } = useNotifications();
  const { status: healthStatus, isBusy, isDisconnected, pendingLongActions } = useHealthStatus();

  // Hook para deduplicação de shortcuts (previne triplicação)
  const { isDuplicate } = useShortcutDeduplication(200);

  // Mapear healthStatus para hostStatus (compatibilidade com código existente)
  const [hostStatus, setHostStatus] = useState<'ok' | 'reconnecting' | 'error' | undefined>();
  const prevHostStatusRef = useRef<'ok' | 'reconnecting' | 'error' | undefined>(undefined);
  const prevHealthStatusRef = useRef<'connected' | 'disconnected' | 'busy' | undefined>(undefined);
  const shortcutsSyncedRef = useRef(false); // Evita sincronização duplicada de atalhos com C#

  // ✅ Estado de atalhos ativos no C# (hookInstalled)
  const [shortcutsActive, setShortcutsActive] = useState(false);
  const [oneAgruparConfirmState, setOneAgruparConfirmState] = useState<{
    selectedObjectsCount: number;
    limit: number;
    tool: any;
  } | null>(null);

  // Converter healthStatus para hostStatus (para manter compatibilidade)
  useEffect(() => {
    // Log quando health status muda (apenas nível DEBUG)
    if (prevHealthStatusRef.current !== undefined &&
      prevHealthStatusRef.current !== healthStatus) {
      logger.debug('[TOOLS_MENU] Health status mudou', {
        from: prevHealthStatusRef.current,
        to: healthStatus,
        isBusy,
        isDisconnected,
        pendingLongActions
      });
    }
    prevHealthStatusRef.current = healthStatus;

    // Mapear para hostStatus
    if (isBusy) {
      // Durante ações longas, considerar como 'ok' (não mostrar erro)
      setHostStatus('ok');
    } else if (isDisconnected) {
      setHostStatus('error');
    } else {
      setHostStatus('ok');
    }
  }, [healthStatus, isBusy, isDisconnected, pendingLongActions]);

  // ✅ OTIMIZAÇÃO: Estado de progresso para UI do SecurityGate
  const [loadingProgress, setLoadingProgress] = useState({
    hasToken: false,
    hasUserInfo: false,
    hasPermissions: false,
    hasShortcuts: false,
  });

  // Usar hooks customizados
  const toolsState = useToolsState();
  const toolExecution = useToolExecution({
    setSendingId: toolsState.setSendingId,
    setOneAgruparRunning: toolsState.setOneAgruparRunning,
    setShowCollectMetadata: toolsState.setShowCollectMetadata,
    setCollectMetadataResult: toolsState.setCollectMetadataResult,
    setErrorMessage: toolsState.setErrorMessage,
    oneAgruparToolData: toolsState.oneAgruparToolData,
    oneAgruparCache: toolsState.oneAgruparCache
  });
  const exportProducaoOffline = useExportProducaoOffline(toolsState.showExportProducaoOffline);

  // Consumir token curto via fragmento ao abrir o Docker 2
  const sso = useSsoFragmentLogin();

  // ✅ SIMPLIFICADO: Usar hooks modulares
  // Hook para validação de segurança
  const { authState, setAuthState } = useSecurityValidator(userInfo);

  // ✅ Estado para escopo de camadas (Page/Document) - Movido para o topo para ser acessível nos efeitos
  const [layerScope, setLayerScope] = useState<'active' | 'document'>('document');

  // ═══════════════════════════════════════════════════════════════════════════
  // BOOTSTRAP & NAVIGATION: Eventos de bootstrap/navegação do MessageRouter
  // ═══════════════════════════════════════════════════════════════════════════
  //
  // ARQUITETURA:
  // - MessageRouter é o ÚNICO listener do window.chrome.webview
  // - MessageRouter emite eventos customizados (bootstrap:received, navigate:to-tool, etc)
  // - useBootstrapEvents escuta esses eventos customizados (NÃO WebView2 diretamente)
  //
  // IMPORTANTE:
  // - Shortcuts são processados separadamente (ver seção SHORTCUTS abaixo)
  // - Este hook NÃO registra listeners no WebView2
  //
  // @see docs/SHORTCUTS_ARCHITECTURE.md
  // ═══════════════════════════════════════════════════════════════════════════

  useBootstrapEvents({
    onNavigateToTool: (toolId) => {
      if (toolId === 'oneregua') {
        toolsState.setShowOneRegua(true);
      } else if (toolId === 'onebitmapv1') {
        toolsState.setShowOneBitmapV1(true);
      } else if (toolId === 'oneagrupar') {
        toolExecution.executeOneAgruparDirect();
      } else if (toolId === 'montatech') {
        toolsState.setShowMontaTech(true);
      }
    }
  });

  // Hook para registrar executores de ferramentas
  useToolExecutorRegistry(registerExecutor, unregisterExecutor, toolsState, toolExecution);


  // Buscar a versão do add-on uma única vez quando a tela de ferramentas ficar pronta.
  // O monitor ativo de conexão já usa app-status; aqui só complementamos a leitura da versão
  // com um pequeno atraso para não colidir com o bootstrap da conexão.
  useEffect(() => {
    if (isBusy || toolsState.appVersion) {
      return;
    }

    let destroyed = false;
    const timerId = window.setTimeout(() => {
      if (destroyed) {
        return;
      }
      try {
        corelCommunication.sendActionGuarded('app-status', {}, (resp: any) => {
          if (destroyed) return;
          const ver = resp?.result?.app?.version
            ?? resp?.result?.app?.addonVersion
            ?? resp?.result?.env?.appVersion
            ?? resp?.data?.env?.appVersion
            ?? null;

          if (resp?.status === 'ok' && ver) {
            toolsState.setAppVersion(ver);
          } else {
            sendToTerminal('⚠️ [DOCKER_TOOLS] Versão não detectada', {
              status: resp?.status,
              hasResult: !!resp?.result
            });
          }
        });
      } catch { }
    }, 800);

    return () => {
      destroyed = true;
      try { window.clearTimeout(timerId); } catch { }
    };
  }, [isBusy, toolsState.appVersion, toolsState.setAppVersion]);

  useEffect(() => {
    // silencioso
  }, [userInfo]);

  // Listener mínimo para fechar UI quando o host sinalizar fechamento do Docker 2
  useEffect(() => {
    const handler = (event: any) => {
      try {
        const raw = typeof event.data === 'string' ? JSON.parse(event.data as string) : event.data;
        if (!raw || typeof raw !== 'object') return;
        if (raw.action === 'docker2:closed') {
          toolsState.closeAllModals();
        }
      } catch { }
    };
    if (window.chrome && window.chrome.webview) {
      window.chrome.webview.addEventListener('message', handler);
      return () => { try { (window.chrome?.webview as any)?.removeEventListener?.('message', handler); } catch { } };
    }
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [toolsState]);

  // Removido: bootstraps legados/localStorage (v2 usa apenas session:*)

  // Registrar executores para atalhos de TODAS as ferramentas
  // IMPORTANTE: O actionId aqui DEVE corresponder ao actionId no tools.registry.ts
  // Para ferramentas com opensModalId, o actionId é 'abrir-modal'
  useEffect(() => {
    const regs = [
      // Ferramentas que abrem modal (usam 'abrir-modal' como actionId)
      // Corresponde a ferramentas com 'opensModalId' no tools.registry.ts
      { toolId: 'oneregua', actionId: 'abrir-modal', exec: () => toolsState.setShowOneRegua(true) },
      { toolId: 'onebitmapv1', actionId: 'abrir-modal', exec: () => toolsState.setShowOneBitmapV1(true) },
      { toolId: 'montatech', actionId: 'abrir-modal', exec: () => toolsState.setShowMontaTech(true) },
      { toolId: 'moldeone-v2', actionId: 'abrir-modal', exec: () => toolsState.setShowMoldeOne(true) },
      { toolId: 'testsmb', actionId: 'abrir-modal', exec: () => toolsState.setShowTestSmb(true) },
      { toolId: 'resize-v2', actionId: 'abrir-modal', exec: () => toolsState.setShowOneResize(true) },

      // Ferramentas com actionId específico (deve corresponder ao tools.registry.ts)
      { toolId: 'criar-retangulo', actionId: 'criar-retangulo', exec: () => { toolExecution.executeCriarRetangulo(); } },
      { toolId: 'oneagrupar', actionId: 'oneagrupar', exec: () => { toolExecution.executeOneAgruparDirect(); } },
      { toolId: 'cmd_montatec', actionId: 'command:1', exec: () => { toolExecution.executeCommand(1); } },
      { toolId: 'cmd_mult_montatec', actionId: 'command:2', exec: () => { toolExecution.executeCommand(2); } },
      { toolId: 'moldesone', actionId: 'command:3', exec: () => { toolExecution.executeCommand(3); } },
      { toolId: 'onecontour', actionId: 'command:4', exec: () => { toolExecution.executeCommand(4); } },
      { toolId: 'resize', actionId: 'command:5', exec: () => { toolExecution.executeCommand(5); } },
      { toolId: 'transparencia', actionId: 'command:6', exec: () => { toolExecution.executeCommand(6); } },
      { toolId: 'smartimport', actionId: 'smartimport', exec: () => { toolExecution.executeSmartImport(); } },
      { toolId: 'open-free-file', actionId: 'open-free-file', exec: () => { toolExecution.executeOpenFreeFile(); } },
      { toolId: 'runps', actionId: 'runps', exec: () => { toolsState.setShowRunPsConfirm(true); } },
      { toolId: 'export_batch_local', actionId: 'export_batch_local', exec: () => { toolExecution.executeExportBatchLocal(); } },
      { toolId: 'export_producao_offline', actionId: 'export_producao_offline', exec: () => { toolsState.setShowExportProducaoOffline(true); } },
      { toolId: 'repair-pdf', actionId: 'repair-pdf', exec: () => { toolExecution.executeRepairPdf(); } },
      { toolId: 'multiplicador-objetos', actionId: 'multiplicador-objetos', exec: () => { toolExecution.executeMultiplicadorObjetos(); } },
      { toolId: 'grid-objetos', actionId: 'grid-objetos', exec: () => { toolExecution.executeGridObjetos(); } },
      { toolId: 'contracao-dtf-textil', actionId: 'contracao-dtf-textil', exec: () => { toolExecution.executeContracaoDtfTextil(); } },
      { toolId: 'contracao-dtf-uv', actionId: 'contracao-dtf-uv', exec: () => { toolExecution.executeContracaoDtfUv(); } },
      { toolId: 'girar-objeto', actionId: 'girar-objeto', exec: () => { toolExecution.executeGirarObjeto(); } },
      { toolId: 'layout-grid', actionId: 'layout-grid', exec: () => { toolExecution.executeLayoutGrid(); } },
      { toolId: 'multiplicar-objeto-v2', actionId: 'multiplicar-objeto-v2', exec: () => { toolExecution.executeMultiplicarObjetoV2(); } },
      { toolId: 'quotation-calculator', actionId: 'quotation-calculator', exec: () => { toolsState.openQuotationCalculator(); } },
      { toolId: 'refresh', actionId: 'refresh', exec: () => { toolsState.setShowRefreshConfirm(true); } },
      { toolId: 'limpar-cache', actionId: 'limpar-cache', exec: () => { toolExecution.executeLimparCache(); } },
      { toolId: 'gerenciar-camadas', actionId: 'gerenciar-camadas', exec: () => { toolExecution.executeGerenciarCamadas(layerScope); } },
    ];

    regs.forEach(r => {
      try {
        registerExecutor(r.toolId, r.actionId, r.exec);
      } catch (err) {
        sendToTerminal('⚠️ [SHORTCUTS] Erro ao registrar executor', {
          toolId: r.toolId,
          actionId: r.actionId,
          error: err instanceof Error ? err.message : String(err)
        });
      }
    });

    return () => {
      regs.forEach(r => {
        try { unregisterExecutor(r.toolId, r.actionId); } catch { }
      });
    };
  }, [registerExecutor, unregisterExecutor, toolsState, toolExecution, layerScope]);

  // Feedback de SSO expirado permanece no ToolsMenu, mas a decisão de authState
  // fica centralizada em useSecurityValidator para evitar máquinas duplicadas.
  useEffect(() => {
    const handleSsoExpired = (event: CustomEvent<{ reason?: string }>) => {
      const reason = event.detail?.reason || 'Sessão expirada';
      notify({
        type: 'error',
        title: 'Sessão Expirada',
        message: 'Sua sessão expirou. Por favor, reabra a janela "Ferramentas" para continuar.',
        duration: 10000
      });
      sendToTerminal('⚠️ [DOCKER_TOOLS] SSO expirado - UI notificada', { reason });
      setAuthState('invalid');
    };

    window.addEventListener('sso:expired', handleSsoExpired as any);
    return () => {
      window.removeEventListener('sso:expired', handleSsoExpired as any);
    };
  }, [notify, setAuthState]);

  // Notificações toast para mudanças de hostStatus
  useEffect(() => {
    // Ignorar se undefined ou se não mudou
    if (!hostStatus || hostStatus === prevHostStatusRef.current) {
      return;
    }

    const previousStatus = prevHostStatusRef.current;
    prevHostStatusRef.current = hostStatus;

    if (hostStatus === 'reconnecting') {
      notify({
        type: 'warning',
        title: 'Host Indisponível',
        message: 'Tentando reconectar…',
        sticky: false
      });
    } else if (hostStatus === 'error') {
      notify({
        type: 'error',
        title: 'Conexão Perdida',
        message: 'Conexão com o host perdida. Feche e reabra as Ferramentas.',
        sticky: false
      });
    } else if (hostStatus === 'ok' && previousStatus !== undefined) {
      // Toast de sucesso apenas se estava em estado de erro antes
      if (previousStatus === 'reconnecting' || previousStatus === 'error') {
        notify({
          type: 'success',
          title: 'Conexão Restabelecida',
          message: 'Host conectado com sucesso.',
          sticky: false
        });
      }
    }
  }, [hostStatus, notify]);

  // Ativar escuta de atalhos globais (não altera comportamento atual)
  useKeyboardShortcuts({ preventDefault: true, ignoreWhenTyping: true, enabled: true });

  // ═══════════════════════════════════════════════════════════════════════════
  // CARREGAR ATALHOS DA API (SEM FALLBACK)
  // ═══════════════════════════════════════════════════════════════════════════

  // ═══════════════════════════════════════════════════════════════════════════
  // CARREGAR ATALHOS DA API (SEM FALLBACK)
  // ✅ ALTERAÇÃO: Não sincroniza automaticamente com C# - aguarda F1 ou toggle manual
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (authState !== 'valid') return;

    // Caso 1: Atalhos já carregados pelo useShortcutsLoader
    if (shortcutsLoader.isLoaded && shortcutsLoader.shortcuts) {
      const contextKeysCount = Object.keys(keyboardContextState || {}).length;

      // Apenas hidratar contexto - NÃO sincronizar com C# (usuário deve apertar F1)
      if (contextKeysCount === 0 && Object.keys(shortcutsLoader.shortcuts).length > 0) {
        hydrate(shortcutsLoader.shortcuts);
        sendToTerminal('ℹ️ [SHORTCUTS] Atalhos carregados no contexto. Pressione F1 para ativar.', {
          bindings: Object.values(shortcutsLoader.shortcuts).reduce(
            (sum, actions) => sum + Object.keys(actions).length, 0
          )
        });
      }
      return;
    }

    // Caso 2: Precisa carregar atalhos
    const loadShortcuts = async () => {
      const state = await shortcutsLoader.load();

      if (state && Object.keys(state).length > 0) {
        // Apenas hidratar contexto - NÃO sincronizar com C# automaticamente
        hydrate(state);
        sendToTerminal('ℹ️ [SHORTCUTS] Atalhos carregados no contexto. Pressione F1 para ativar.', {
          bindings: Object.values(state).reduce(
            (sum, actions) => sum + Object.keys(actions).length, 0
          )
        });
      }
    };

    loadShortcuts();
  }, [authState, shortcutsLoader.isLoaded, shortcutsLoader.isLoading, shortcutsLoader.error, shortcutsLoader.shortcuts, shortcutsLoader.load, hydrate, userInfo, notify]);

  // ✅ OTIMIZAÇÃO: Notificar erro ao carregar atalhos com mensagem amigável
  useEffect(() => {
    if (shortcutsLoader.error && authState === 'valid') {
      // ✅ DEBUG: Log detalhado do erro de atalhos
      sendToTerminal('[TOOLS_MENU] Erro ao carregar atalhos - notificando usuário', {
        error: shortcutsLoader.error,
        authState,
        isLoaded: shortcutsLoader.isLoaded,
        isLoading: shortcutsLoader.isLoading
      });
      
      notifyUserFriendlyError(
        shortcutsLoader.error,
        notify,
        'carregar atalhos de teclado',
        {
          sticky: false, // Não bloquear - ferramentas funcionam sem atalhos
          duration: 8000
        }
      );
    }
  }, [shortcutsLoader.error, authState, notify]);

  // ═══════════════════════════════════════════════════════════════════════════
  // SHORTCUTS: Fluxo de processamento de atalhos de teclado
  // ═══════════════════════════════════════════════════════════════════════════
  //
  // ARQUITETURA:
  // 1. Backend C# detecta atalho → envia mensagem (type: "shortcut")
  // 2. MessageRouter (ÚNICO listener) → recebe e dispara "shortcut:received"
  // 3. Listener abaixo → recebe "shortcut:received", parseia e dispara "shortcut:execute"
  // 4. Listener final → recebe "shortcut:execute", deduplica e executa
  //
  // IMPORTANTE:
  // - MessageRouter é o ÚNICO ponto que escuta mensagens do backend
  // - useBootstrapListener NÃO processa shortcuts (apenas bootstrap, navigate, etc)
  // - Deduplicação previne execução múltipla se houver listeners duplicados
  //
  // @see docs/SHORTCUTS_ARCHITECTURE.md para documentação completa
  //
  // ═══════════════════════════════════════════════════════════════════════════

  // Listener para receber atalhos do MessageRouter (shortcut:received)
  useEffect(() => {
    const handleShortcutReceived = (event: Event) => {
      const customEvent = event as CustomEvent<{
        action: string;
        combo: string;
        ts: string;
        _debug_publishId?: number;
      }>;
      const { action, combo, _debug_publishId } = customEvent.detail || {};

      if (!action) {
        sendToTerminal('⚠️ [SHORTCUT] Evento recebido sem action', { combo });
        return;
      }

      // Parsear action no formato "toolId::actionId"
      const parsed = parseShortcutAction(action);
      if (parsed) {
        sendToTerminal('⌨️ [SHORTCUT] Convertendo para shortcut:execute', {
          combo,
          action,
          toolId: parsed.toolId,
          actionId: parsed.actionId,
          _debug_publishId // Para rastreamento de duplicatas
        });

        // Disparar evento de execução
        window.dispatchEvent(new CustomEvent('shortcut:execute', {
          detail: {
            toolId: parsed.toolId,
            actionId: parsed.actionId,
            _debug_publishId
          }
        }));
      } else {
        sendToTerminal('⚠️ [SHORTCUT] Formato de action inválido', { action, combo });
      }
    };

    window.addEventListener('shortcut:received', handleShortcutReceived);
    return () => window.removeEventListener('shortcut:received', handleShortcutReceived);
  }, []);

  // Listener para executar atalhos (shortcut:execute)
  // ✅ PROTEÇÃO: Deduplicação previne execução múltipla quando há listeners duplicados
  useEffect(() => {
    const handleExecute = (event: Event) => {
      const customEvent = event as CustomEvent<{
        toolId: string;
        actionId: string;
        _debug_publishId?: number;
      }>;
      const { toolId, actionId, _debug_publishId } = customEvent.detail || {};

      if (toolId && actionId) {
        // Verificar se é duplicata (previne triplicação por listeners duplicados)
        if (isDuplicate(toolId, actionId)) {
          return; // Ignorar duplicata
        }

        sendToTerminal('▶️ [SHORTCUT] Executando atalho', {
          toolId,
          actionId,
          _debug_publishId // Para rastreamento de duplicatas
        });
        executeShortcut(toolId, actionId);
      }
    };

    window.addEventListener('shortcut:execute', handleExecute);
    return () => window.removeEventListener('shortcut:execute', handleExecute);
  }, [executeShortcut, isDuplicate]);

  // Cleanup: desabilitar atalhos ao fechar/sair
  useEffect(() => {
    const handleBeforeUnload = () => {
      disableShortcutsInBackend();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Também desabilitar no unmount do componente
      disableShortcutsInBackend();
    };
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // ATALHO MESTRE (F1) - Toggle de atalhos fora do escopo do sistema
  // Este atalho funciona localmente, não é sincronizado com C#
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    const handleMasterToggle = (e: KeyboardEvent) => {
      // Ignorar quando digitando em inputs/textareas
      if (isTypingTarget(e.target)) return;
      
      // F1 como atalho mestre
      if (e.key === 'F1') {
        e.preventDefault();
        e.stopPropagation();
        
        // Toggle do estado - chama o mesmo handler do botão
        handleShortcutsToggle(!shortcutsActive);
      }
    };

    document.addEventListener('keydown', handleMasterToggle);
    sendToTerminal('⌨️ [SHORTCUTS] Atalho mestre F1 registrado (local only)');
    
    return () => {
      document.removeEventListener('keydown', handleMasterToggle);
    };
  }, [shortcutsActive]); // Dependência em shortcutsActive para ter o valor atual

  // ═══════════════════════════════════════════════════════════════════════════
  // TOGGLE DE ATALHOS - Habilitar/Desabilitar manualmente
  // ═══════════════════════════════════════════════════════════════════════════
  const handleShortcutsToggle = async (enable: boolean) => {
    if (enable) {
      // Habilitar atalhos
      sendToTerminal('🔄 [SHORTCUTS] Habilitando atalhos manualmente...');

      // ✅ CORREÇÃO: Usar estado do KeyboardContext (hidratado) em vez de shortcutsLoader
      const shortcuts = keyboardContextState;
      if (!shortcuts || Object.keys(shortcuts).length === 0) {
        notify({
          type: 'warning',
          title: 'Sem atalhos',
          message: 'Nenhum atalho configurado para habilitar.',
          duration: 3000
        });
        return;
      }

      const result = await syncShortcutsWithVerification(shortcuts, {
        forceEnable: true,
        onFailedShortcuts: (failed) => {
          if (failed.length > 0) {
            const failedCombos = failed.map(f => f.combo).join(', ');
            notify({
              type: 'warning',
              title: 'Alguns atalhos falharam',
              message: `Os seguintes atalhos não foram registrados: ${failedCombos}`,
              duration: 5000
            });
          }
        }
      });

      setShortcutsActive(result.hookInstalled);

      if (result.hookInstalled) {
        notify({
          type: 'success',
          title: 'Atalhos Habilitados',
          message: `${result.totalRegistered} atalho(s) ativado(s) com sucesso.`,
          duration: 3000
        });
      } else {
        notify({
          type: 'error',
          title: 'Erro ao habilitar',
          message: result.error || 'Não foi possível habilitar os atalhos.',
          duration: 5000
        });
      }
    } else {
      // Desabilitar atalhos
      sendToTerminal('🔄 [SHORTCUTS] Desabilitando atalhos manualmente...');

      const result = await disableShortcutsWithVerification();

      if (result.success) {
        setShortcutsActive(false);
        notify({
          type: 'info',
          title: 'Atalhos Desabilitados',
          message: 'Atalhos de teclado foram desativados.',
          duration: 3000
        });
      } else {
        notify({
          type: 'error',
          title: 'Erro ao desabilitar',
          message: result.error || 'Não foi possível desabilitar os atalhos.',
          duration: 5000
        });
      }
    }
  };

  // ✅ Ref para evitar logs duplicados e re-execuções
  const hasLoggedReadyRef = useRef(false);
  const readyCheckInitializedRef = useRef(false);

  // ═══════════════════════════════════════════════════════════════════════════
  // ✅ SOCKET WARM-UP: garante que o socket esteja conectado quando a tela de
  // ferramentas estiver pronta. Evita o caso "cliquei em Calcular e o socket
  // estava morto" do MontaTech (e quaisquer outras ferramentas que dependem
  // dele). NÃO desconecta no cleanup — o singleton é compartilhado com outras
  // páginas e seu ciclo de vida é gerenciado pelo WebSocketContext.
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (authState !== 'valid') return;

    let cancelled = false;
    const warmUp = async () => {
      if (socketService.isConnected()) return;
      const result = await socketService.ensureConnected(8000);
      if (cancelled) return;
      if (!result.ok) {
        sendToTerminal('⚠️ [TOOLS_MENU] Socket warm-up falhou (ferramentas que usam WebSocket podem demorar mais)', {
          reason: result.reason,
          message: result.message,
        });
      } else {
        sendToTerminal('🔌 [TOOLS_MENU] Socket pronto para uso das ferramentas');
      }
    };

    warmUp();
    return () => { cancelled = true; };
  }, [authState]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ✅ OTIMIZAÇÃO: Ready state com timeouts adaptativos e atualização de progresso
  // - Intervalo rápido: 50ms (vs 100ms antes)
  // - Timeout L1: 1s (alerta)
  // - Timeout L2: 2s (forçar ready se possível)
  // - Timeout máximo: 2.5s (vs 3s antes)
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    // Se authState não é válido, não está pronto
    if (authState !== 'valid') {
      toolsState.setIsReady(false);
      hasLoggedReadyRef.current = false;
      readyCheckInitializedRef.current = false;
      return;
    }

    // Se já está pronto e já logou, não fazer nada
    if (toolsState.isReady && hasLoggedReadyRef.current) {
      return;
    }

    // Função para verificar e atualizar progresso
    const checkAndUpdateProgress = () => {
      const hasToken = !!(window as any)?.sessionToken || !!TokenService.getAccessToken();
      const hasUserInfoNow = !!userInfo;
      const hasPermissions = !!userInfo?.permissionsConfig && userInfo.permissionsConfig.length > 0;
      const hasShortcuts = shortcutsLoader.isLoaded || !!shortcutsLoader.error;

      // ✅ Atualizar progresso para UI
      setLoadingProgress({ hasToken, hasUserInfo: hasUserInfoNow, hasPermissions, hasShortcuts });

      // Está pronto se tem token + userInfo + (permissões OU atalhos)
      const ready = hasToken && hasUserInfoNow && (hasPermissions || hasShortcuts);

      return { ready, hasToken, hasUserInfo: hasUserInfoNow, hasPermissions, hasShortcuts };
    };

    // Verificar imediatamente
    const initialCheck = checkAndUpdateProgress();
    if (initialCheck.ready && !hasLoggedReadyRef.current) {
      hasLoggedReadyRef.current = true;
      toolsState.setIsReady(true);
      sendToTerminal('✅ [TOOLS_READY] Sistema pronto - todos os processos concluídos', {
        ...initialCheck,
        phase: 'immediate'
      });
      return;
    }

    // Se já inicializou a verificação, não criar novos intervalos
    if (readyCheckInitializedRef.current) {
      return;
    }
    readyCheckInitializedRef.current = true;

    // ✅ SIMPLIFICADO: Apenas 2 timeouts fixos (sem L2 "forçar ready")
    let timeoutWarning: NodeJS.Timeout | null = null;
    let timeoutFinal: NodeJS.Timeout | null = null;
    let checkIntervalId: NodeJS.Timeout | null = null;

    const clearAllTimers = () => {
      if (checkIntervalId) clearInterval(checkIntervalId);
      if (timeoutWarning) clearTimeout(timeoutWarning);
      if (timeoutFinal) clearTimeout(timeoutFinal);
    };

    // ✅ SIMPLIFICADO: Verifica se está pronto (TODOS os dados necessários)
    const setReadyIfPossible = () => {
      if (hasLoggedReadyRef.current) {
        clearAllTimers();
        return;
      }

      const progress = checkAndUpdateProgress();

      // ✅ DETERMINÍSTICO: Só marca ready se TUDO estiver pronto
      if (progress.ready) {
        hasLoggedReadyRef.current = true;
        toolsState.setIsReady(true);

        // ✅ OTIMIZAÇÃO: Mensagem amigável se atalhos falharam
        if (shortcutsLoader.error && !shortcutsLoader.isLoaded) {
          // ✅ DEBUG: Log do erro de atalhos no ready state
          sendToTerminal('[TOOLS_MENU] Atalhos falharam mas sistema está pronto - notificando', {
            error: shortcutsLoader.error,
            progress,
            retryCount: retryCountRef.current
          });
          
          notifyUserFriendlyError(
            shortcutsLoader.error,
            notify,
            'carregar atalhos',
            {
              sticky: false,
              duration: 6000
            }
          );
        }

        sendToTerminal('✅ [TOOLS_READY] Sistema pronto - todos os dados carregados', {
          ...progress,
          phase: 'success'
        });

        clearAllTimers();
      }
    };

    // ✅ OTIMIZAÇÃO: Intervalo rápido de 50ms para detectar quando fica pronto
    checkIntervalId = setInterval(setReadyIfPossible, 50);

    // ✅ TIMEOUT WARNING: 1.5s - apenas alerta visual no log
    timeoutWarning = setTimeout(() => {
      if (hasLoggedReadyRef.current) return;
      const progress = checkAndUpdateProgress();

      sendToTerminal('⚠️ [TOOLS_READY] Ainda carregando após 1.5s...', {
        ...progress,
        phase: 'warning'
      });
    }, 1500);

    // ✅ TIMEOUT FINAL: 3s - ou está pronto OU mostra erro claro (sem "forçar parcial")
    timeoutFinal = setTimeout(() => {
      if (hasLoggedReadyRef.current) {
        clearAllTimers();
        return;
      }

      hasLoggedReadyRef.current = true;
      const progress = checkAndUpdateProgress();

      // ✅ DETERMINÍSTICO: Verifica se TEM dados mínimos (token + userInfo)
      if (progress.hasToken && progress.hasUserInfo) {
        // Tem dados essenciais - marcar como pronto
        toolsState.setIsReady(true);

        sendToTerminal('✅ [TOOLS_READY] Sistema pronto (timeout final - dados essenciais OK)', {
          ...progress,
          phase: 'timeout-final-ok',
          note: 'Permissões/atalhos podem estar pendentes mas aplicação funcionará'
        });
      } else {
        // NÃO tem dados essenciais - FALHA CRÍTICA
        toolsState.setIsReady(false);
        setAuthState('invalid'); // Marca auth como inválido

        // ✅ OTIMIZAÇÃO: Mensagem clara de erro
        notifyUserFriendlyError(
          { message: 'Falha ao carregar dados de autenticação. Tente recarregar a página.' },
          notify,
          'inicializar Ferramentas',
          {
            sticky: true,
            actions: [
              {
                label: 'Recarregar Página',
                onClick: () => window.location.reload()
              }
            ]
          }
        );

        sendToTerminal('❌ [TOOLS_READY] FALHA CRÍTICA - Dados essenciais ausentes após 3s', {
          ...progress,
          phase: 'timeout-final-error',
          missingCritical: {
            token: !progress.hasToken,
            userInfo: !progress.hasUserInfo
          }
        });
      }

      clearAllTimers();
    }, 3000);

    return () => {
      clearAllTimers();
      readyCheckInitializedRef.current = false;
    };
  }, [authState, userInfo, shortcutsLoader.isLoaded, shortcutsLoader.error, toolsState, notify]);



  const handleToolClick = async (id: string) => {
    try {
      if (toolsState.sendingId) return;
      const def = toolsRegistry.find(t => t.id === id);
      if (def?.disabled) return;
      // Bloqueio de ações quando add-on está desatualizado
      if (def?.actionId && toolsState.appVersion && !isVersionAtLeast(toolsState.appVersion, toolsState.minAddonVersion)) {
        toolsState.setErrorMessage(`Add‑on desatualizado neste Docker. Versão instalada ${toolsState.appVersion}; mínimo recomendado ${toolsState.minAddonVersion}. Atualize o add‑on e reinicie o Corel.`);
        return;
      }
      if (id === 'onebitmap') {
        // Botão desabilitado - não fazer nada
        return;
      }
      if (id === 'runps') {
        const skipConfirm = toolsState.runPsDontAsk;
        if (skipConfirm) {
          await toolExecution.executeRunPs();
        } else {
          toolsState.setShowRunPsConfirm(true);
        }
        return;
      }
      if (id === 'collectmetadata') {
        await toolExecution.executeCollectMetadata();
        return;
      }
      if (id === 'smartimport') {
        await toolExecution.executeSmartImport();
        return;
      }
      if (id === 'open-free-file') {
        toolsState.markBusy('open-free-file');
        try {
          await toolExecution.executeOpenFreeFile();
        } finally {
          toolsState.unmarkBusy('open-free-file');
        }
        return;
      }
      if (id === 'export_batch_local') {
        toolsState.markBusy('export_batch_local');
        try {
          await toolExecution.executeExportBatchLocal();
        } finally {
          toolsState.unmarkBusy('export_batch_local');
        }
        return;
      }
      if (id === 'export_producao_offline') {
        toolsState.setShowExportProducaoOffline(true);
        return;
      }
      if (id === 'repair-pdf') {
        toolsState.markBusy('repair-pdf');
        try {
          await toolExecution.executeRepairPdf();
        } finally {
          toolsState.unmarkBusy('repair-pdf');
        }
        return;
      }
      if (id === 'oneregua') {
        toolsState.setShowOneRegua(true);
        return;
      }
      if (id === 'onebitmapv1') {
        toolsState.setShowOneBitmapV1(true);
        return;
      }
      if (id === 'testsmb') {
        toolsState.setShowTestSmb(true);
        return;
      }
      if (id === 'resize-v2') {
        toolsState.setShowOneResize(true);
        return;
      }
      if (id === 'criar-retangulo') {
        toolsState.markBusy('criar-retangulo');
        try { await toolExecution.executeCriarRetangulo(); } finally { toolsState.unmarkBusy('criar-retangulo'); }
        return;
      }
      if (id === 'oneagrupar') {
        toolsState.markBusy('oneagrupar');
        try {
          const prepared = await toolExecution.prepareOneAgruparExecution();
          if (prepared?.precheck?.requiresConfirmation) {
            setOneAgruparConfirmState({
              selectedObjectsCount: prepared.precheck.selectedObjectsCount,
              limit: prepared.precheck.limit,
              tool: prepared.tool
            });
          } else if (prepared?.tool) {
            await toolExecution.executePreparedOneAgrupar(prepared.tool);
          }
        } finally {
          toolsState.unmarkBusy('oneagrupar');
        }
        return;
      }
      if (id === 'montatech') {
        toolsState.setShowMontaTech(true);
        return;
      }
      if (id === 'moldeone-v2') {
        toolsState.setShowMoldeOne(true);
        return;
      }
      if (id === 'cmd_montatec') {
        toolsState.markBusy('cmd_montatec');
        try { await toolExecution.executeCommand(1); } finally { toolsState.unmarkBusy('cmd_montatec'); }
        return;
      }
      if (id === 'cmd_mult_montatec') {
        toolsState.markBusy('cmd_mult_montatec');
        try { await toolExecution.executeCommand(2); } finally { toolsState.unmarkBusy('cmd_mult_montatec'); }
        return;
      }
      if (id === 'moldesone') {
        toolsState.markBusy('moldesone');
        try { await toolExecution.executeCommand(3); } finally { toolsState.unmarkBusy('moldesone'); }
        return;
      }
      if (id === 'onecontour') {
        toolsState.markBusy('onecontour');
        try { await toolExecution.executeCommand(4); } finally { toolsState.unmarkBusy('onecontour'); }
        return;
      }
      if (id === 'onecontour-v2') {
        toolsState.setShowOneContorno(true);
        return;
      }
      if (id === 'resize') {
        toolsState.markBusy('resize');
        try { await toolExecution.executeCommand(5); } finally { toolsState.unmarkBusy('resize'); }
        return;
      }
      if (id === 'transparencia') {
        toolsState.markBusy('transparencia');
        try { await toolExecution.executeCommand(6); } finally { toolsState.unmarkBusy('transparencia'); }
        return;
      }
      if (id === 'multiplicador-objetos') {
        toolsState.markBusy('multiplicador-objetos');
        try {
          await toolExecution.executeMultiplicadorObjetos();
        } finally {
          toolsState.unmarkBusy('multiplicador-objetos');
        }
        return;
      }
      if (id === 'grid-objetos') {
        toolsState.markBusy('grid-objetos');
        try {
          await toolExecution.executeGridObjetos();
        } finally {
          toolsState.unmarkBusy('grid-objetos');
        }
        return;
      }
      if (id === 'contracao-dtf-textil') {
        toolsState.markBusy('contracao-dtf-textil');
        try {
          await toolExecution.executeContracaoDtfTextil();
        } finally {
          toolsState.unmarkBusy('contracao-dtf-textil');
        }
        return;
      }
      if (id === 'contracao-dtf-uv') {
        toolsState.markBusy('contracao-dtf-uv');
        try {
          await toolExecution.executeContracaoDtfUv();
        } finally {
          toolsState.unmarkBusy('contracao-dtf-uv');
        }
        return;
      }
      if (id === 'girar-objeto') {
        toolsState.markBusy('girar-objeto');
        try {
          await toolExecution.executeGirarObjeto();
        } finally {
          toolsState.unmarkBusy('girar-objeto');
        }
        return;
      }
      if (id === 'layout-grid') {
        toolsState.markBusy('layout-grid');
        try {
          await toolExecution.executeLayoutGrid();
        } finally {
          toolsState.unmarkBusy('layout-grid');
        }
        return;
      }
      if (id === 'multiplicar-objeto-v2') {
        toolsState.markBusy('multiplicar-objeto-v2');
        try {
          await toolExecution.executeMultiplicarObjetoV2();
        } finally {
          toolsState.unmarkBusy('multiplicar-objeto-v2');
        }
        return;
      }
      if (id === 'quotation-calculator') {
        // ✅ CORREÇÃO: Evitar abertura dupla se já está aberto
        if (toolsState.showQuotationCalculator) {
          sendToTerminal('⚠️ [TOOLS] quotation-calculator já está aberto, ignorando clique', {}, LogCategory.BUSINESS);
          return;
        }
        toolsState.setShowQuotationCalculator(true);
        return;
      }
      if (id === 'refresh') {
        toolsState.setShowRefreshConfirm(true);
        return;
      }
      if (id === 'limpar-cache') {
        toolsState.markBusy('limpar-cache');
        try { await toolExecution.executeLimparCache(); } finally { toolsState.unmarkBusy('limpar-cache'); }
        return;
      }
      if (id === 'gerenciar-camadas') {
        toolsState.markBusy('gerenciar-camadas');
        // ✅ Passar o escopo atual para o executor
        try { await toolExecution.executeGerenciarCamadas(layerScope); } finally { toolsState.unmarkBusy('gerenciar-camadas'); }
        return;
      }
      if (id === 'close-document') {
        toolsState.setShowCloseDocumentConfirm(true);
        return;
      }
      if (id === 'auto-bitmap') {
        toolsState.markBusy('auto-bitmap');
        try { await toolExecution.executeAutoBitmap(); } finally { toolsState.unmarkBusy('auto-bitmap'); }
        return;
      }
      if (id === 'montatech-native') {
        toolsState.markBusy('montatech-native');
        try { await toolExecution.executeMontaTechNative(); } finally { toolsState.unmarkBusy('montatech-native'); }
        return;
      }
      if (id === 'reverter-pipeline') {
        toolsState.markBusy('reverter-pipeline');
        try { await toolExecution.executeReverterPipeline(); } finally { toolsState.unmarkBusy('reverter-pipeline'); }
        return;
      }
    } catch (error: any) {
      // ✅ OTIMIZAÇÃO: Tratamento de erro amigável
      const friendlyError = formatErrorMessage(error, 'executar ferramenta');
      toolsState.setErrorMessage(friendlyError);

      // ✅ DEBUG: Log detalhado do erro na execução da ferramenta
      sendToTerminal('[TOOLS_MENU] Erro ao executar ferramenta', {
        error: error?.message || error,
        errorType: typeof error,
        errorStack: error?.stack?.substring(0, 1000) || 'sem stack',
        toolId: toolsState.sendingId,
        friendlyError
      }, 'error');

      notifyUserFriendlyError(
        error,
        notify,
        'executar ferramenta',
        {
          sticky: false,
          duration: 8000
        }
      );
      toolsState.setSendingId(null);
    }
  };

  const handleRunPsDontAskToggle = (checked: boolean) => {
    toolsState.setRunPsDontAsk(checked);
    try { window.localStorage.setItem('cdr:tools:runps:noAsk', String(checked)); } catch { }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ✅ OTIMIZAÇÃO: Renderização condicional - só mostra página quando isReady
  // ═══════════════════════════════════════════════════════════════════════════

  // Se ainda está validando, mostrar SecurityGate com estado 'checking'
  if (authState === 'checking') {
    return <SecurityGate state="checking" />;
  }

  // Se inválido, mostrar SecurityGate com estado 'invalid'
  if (authState === 'invalid') {
    return <SecurityGate state="invalid" />;
  }

  // Se authState é valid mas ainda não está pronto, mostrar SecurityGate com estado 'loading'
  // Isso garante que a página só é renderizada quando TUDO estiver pronto
  if (!toolsState.isReady) {
    return <SecurityGate state="loading" loadingProgress={loadingProgress} />;
  }

  // ✅ A partir daqui, isReady === true - renderizar página completa
  return (
    <div className="h-screen w-full bg-navy-900 text-gray-200 flex flex-col">
      <ToolsHeader
        onShowShortcuts={() => toolsState.setShowShortcuts(true)}
      />
      <main
        className="p-6 flex-1 overflow-y-auto overflow-x-hidden tools-menu-scroll"
        style={{
          scrollBehavior: 'smooth',
        }}
      >
        <OutdatedBanner
          appVersion={toolsState.appVersion}
          minAddonVersion={toolsState.minAddonVersion}
        />

        <div className="max-w-6xl mx-auto">
          {toolsState.showQuotationCalculator ? (
            <Suspense fallback={<div className="flex items-center justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400" /></div>}>
              <QuotationCalculatorPage
                key={toolsState.quotationInstanceKey}
                onBack={() => {
                  toolsState.setShowQuotationCalculator(false);
                }}
              />
            </Suspense>
          ) : (
            <ToolsGrid
              isReady={true} // ✅ Sempre true aqui - página só renderiza quando isReady
              sendingId={toolsState.sendingId}
              oneAgruparRunning={toolsState.oneAgruparRunning}
              onToolClick={handleToolClick}
              busyToolIds={toolsState.busyToolIds}
              shortcutsActive={shortcutsActive}
              layersUnlocked={toolExecution.isLayersUnlocked}
              // ✅ Props para escopo de camadas
              layerScope={layerScope}
              onSetLayerScope={setLayerScope}
            />
          )}
        </div>
      </main>
      {/* Footer global com toggle de atalhos no slot esquerdo */}
      <GlobalFooter
        leftSlot={
          <ShortcutsToggle
            enabled={shortcutsActive}
            onToggle={handleShortcutsToggle}
            disabled={!toolsState.isReady || shortcutsLoader.isLoading}
          />
        }
      />
      <RunPsConfirmModal
        isOpen={toolsState.showRunPsConfirm}
        onClose={() => toolsState.setShowRunPsConfirm(false)}
        onConfirm={async () => {
          toolsState.setShowRunPsConfirm(false);
          await toolExecution.executeRunPs();
        }}
        runPsDontAsk={toolsState.runPsDontAsk}
        onToggleDontAsk={handleRunPsDontAskToggle}
      />
      <RefreshConfirmModal
        isOpen={toolsState.showRefreshConfirm}
        onClose={() => toolsState.setShowRefreshConfirm(false)}
        onConfirm={async () => {
          toolsState.setShowRefreshConfirm(false);
          toolsState.markBusy('refresh');
          try { await toolExecution.executeRefresh(); } finally { toolsState.unmarkBusy('refresh'); }
        }}
      />
      <CloseDocumentConfirmModal
        isOpen={toolsState.showCloseDocumentConfirm}
        onClose={() => toolsState.setShowCloseDocumentConfirm(false)}
        onConfirm={async () => {
          toolsState.setShowCloseDocumentConfirm(false);
          toolsState.markBusy('close-document');
          try { await toolExecution.executeCloseDocument(); } finally { toolsState.unmarkBusy('close-document'); }
        }}
      />
      <ExportProducaoOfflineModal
        isOpen={toolsState.showExportProducaoOffline}
        isExporting={exportProducaoOffline.isExporting}
        documentInfo={exportProducaoOffline.docContext.documentInfo}
        documentLoading={exportProducaoOffline.docContext.documentLoading}
        parsedOrcId={exportProducaoOffline.docContext.parsedOrcId}
        parsedOitId={exportProducaoOffline.docContext.parsedOitId}
        parsedMaterial={exportProducaoOffline.docContext.parsedMaterial}
        inferredServiceId={exportProducaoOffline.docContext.inferredServiceId}
        onClose={() => {
          if (!exportProducaoOffline.isExporting) {
            toolsState.setShowExportProducaoOffline(false);
          }
        }}
        onConfirm={async (options) => {
          toolsState.markBusy('export_producao_offline');
          try {
            const result = await exportProducaoOffline.runExport(options);
            if (result.success) {
              toolsState.setShowExportProducaoOffline(false);
            }
          } finally {
            toolsState.unmarkBusy('export_producao_offline');
          }
        }}
      />
      {oneAgruparConfirmState && (
        <ConfirmModal
          title="Seleção muito grande"
          message={`Seleção excedeu o limite de ${oneAgruparConfirmState.limit.toLocaleString('pt-BR')} objetos. O "OneAgrupar" pode causar lentidão ou travar o CorelDRAW. Deseja prosseguir?`}
          confirmLabel="Executar"
          cancelLabel="Cancelar"
          onCancel={() => {
            sendToTerminal('❎ [ONEAGRUPAR:PRECHECK] Usuário cancelou execução');
            setOneAgruparConfirmState(null);
          }}
          onConfirm={async () => {
            const pendingTool = oneAgruparConfirmState.tool;
            sendToTerminal('✅ [ONEAGRUPAR:PRECHECK] Usuário confirmou execução');
            setOneAgruparConfirmState(null);
            if (pendingTool) {
              toolsState.markBusy('oneagrupar');
              try {
                await toolExecution.executePreparedOneAgrupar(pendingTool);
              } finally {
                toolsState.unmarkBusy('oneagrupar');
              }
            }
          }}
        />
      )}
      <ToolPageModal
        isOpen={toolsState.showOneRegua}
        onClose={() => toolsState.setShowOneRegua(false)}
        title="OneRégua"
      >
        <OneReguaPage />
      </ToolPageModal>

      <ToolPageModal
        isOpen={toolsState.showOneBitmap}
        onClose={() => toolsState.setShowOneBitmap(false)}
        title="OneBitmap"
      >
        <OneBitmapPage />
      </ToolPageModal>

      <ToolPageModal
        isOpen={toolsState.showOneBitmapV1}
        onClose={() => toolsState.setShowOneBitmapV1(false)}
        title="OneBitmap v1"
      >
        <OneBitmapV1Page />
      </ToolPageModal>

      <ToolPageModal
        isOpen={toolsState.showTestSmb}
        onClose={() => toolsState.setShowTestSmb(false)}
        title="Testar Conexões"
      >
        <TestSmbPage />
      </ToolPageModal>

      <ToolPageModal
        isOpen={toolsState.showMontaTech}
        onClose={() => toolsState.setShowMontaTech(false)}
        title="MontaTech v2"
      >
        <MontaTechPage
          onBack={() => toolsState.setShowMontaTech(false)}
          onPopout={() => {
            const url = `${window.location.origin}/tools/montatech`;
            sendToTerminal('[MONTATECH] Abrindo em docker separado', { url });
            corelCommunication.sendActionGuarded(
              'docker:open-pool',
              { url, reuseKey: 'montatech-expanded' }
            );
            toolsState.setShowMontaTech(false);
          }}
        />
      </ToolPageModal>

      <ToolPageModal
        isOpen={toolsState.showMoldeOne}
        onClose={() => toolsState.setShowMoldeOne(false)}
        title="MoldeOne v3"
      >
        <MoldeOnePage />
      </ToolPageModal>

      <ToolPageModal
        isOpen={toolsState.showOneResize}
        onClose={() => toolsState.setShowOneResize(false)}
        title="OneResize"
      >
        <OneResizePage />
      </ToolPageModal>

      <ToolPageModal
        isOpen={toolsState.showOneContorno}
        onClose={() => toolsState.setShowOneContorno(false)}
        title="OneContorno"
      >
        <OneContornoPage />
      </ToolPageModal>

      <CollectMetadataModal
        isOpen={toolsState.showCollectMetadata}
        onClose={() => {
          toolsState.setShowCollectMetadata(false);
          toolsState.setCollectMetadataResult(null);
        }}
        data={toolsState.collectMetadataResult}
      />
      <ErrorModal
        open={!!toolsState.errorMessage}
        title={(() => {
          // Extrair título da mensagem se contiver "💡" (formato amigável)
          const msg = toolsState.errorMessage || '';
          if (msg.includes('💡')) {
            const lines = msg.split('\n\n');
            const firstLine = lines[0] || '';
            // Se começa com título seguido de dois pontos, usar como título
            if (firstLine.includes(':')) {
              return firstLine.split(':')[0].trim();
            }
          }
          return 'Aviso';
        })()}
        message={toolsState.errorMessage || ''}
        onClose={() => toolsState.setErrorMessage(null)}
        onRetry={(() => {
          // Adicionar retry se a mensagem sugerir recarregar
          const msg = (toolsState.errorMessage || '').toLowerCase();
          if (msg.includes('recarregue') || msg.includes('recarregar')) {
            return () => window.location.reload();
          }
          return undefined;
        })()}
      />

      {/* Modal de Configuração de Atalhos */}
      {toolsState.showShortcuts && (
        <ShortcutConfig onClose={() => toolsState.setShowShortcuts(false)} />
      )}
    </div>
  );
}


